import { supabase } from '@/integrations/supabase/client';

// ============================================
// MAPEAMENTO DE STAGES (VALIDADO)
// ============================================

export const DEAL_STAGE_MAPPING: Record<string, 'new' | 'contact' | 'scheduled' | 'proposal' | 'negotiation' | 'won' | 'lost'> = {
  // Zoho PT-BR
  'Em contato': 'new',
  'Qualificação': 'contact',
  'Análise de Necessidades': 'contact',
  'Proposta': 'proposal',
  'Negociação': 'negotiation',
  'Fechado': 'won',
  'Perdido': 'lost',
  
  // Zoho EN
  'Prospecting': 'new',
  'Qualification': 'contact',
  'Needs Analysis': 'contact',
  'Value Proposition': 'proposal',
  'Id. Decision Makers': 'proposal',
  'Perception Analysis': 'proposal',
  'Proposal/Price Quote': 'proposal',
  'Negotiation/Review': 'negotiation',
  'Closed Won': 'won',
  'Closed Lost': 'lost',
  'Draft': 'new',
  
  // Fallback
  '': 'new',
};

export const LEAD_STATUS_MAPPING: Record<string, string> = {
  // Zoho PT-BR
  'Novo': 'new',
  'Tentando Contatar': 'attempting_contact',
  'Contatado': 'contacted',
  'Qualificado': 'qualified',
  'Não Qualificado': 'unqualified',
  'Não Convertido': 'not_converted',
  'Convertido': 'converted',
  'Perdido': 'lost',
  
  // Zoho EN
  'New': 'new',
  'Attempting to Contact': 'attempting_contact',
  'Contacted': 'contacted',
  'Qualified': 'qualified',
  'Unqualified': 'unqualified',
  'Not Converted': 'not_converted',
  'Converted': 'converted',
  'Lost': 'lost',
  'Junk Lead': 'unqualified',
  'Pre-Qualified': 'qualified',
  
  // Fallback
  '': 'new',
};

export const ROLE_MAPPING: Record<string, string> = {
  'CEO': 'ceo',
  'Manager': 'manager',
  'Standard': 'standard',
  'Administrator': 'admin',
  'Sales Rep': 'sales',
  'Support Rep': 'support',
  // Fallback
  '': 'standard',
};

// ============================================
// TIPOS
// ============================================

interface ZohoUser {
  'Record Id': string;
  'First Name': string;
  'Last Name': string;
  'Email': string;
  'Role': string;
  'Status': string;
  'Display Name'?: string;
}

interface ZohoContact {
  'Record Id': string;
  'First Name': string;
  'Last Name': string;
  'Email': string;
  'Mobile': string;
  'Date of Birth': string;
  'Lead Source': string;
  'Mailing Street': string;
  'Mailing City': string;
  'Mailing State': string;
  'Mailing Zip': string;
  'Mailing Country': string;
  'Created Time': string;
  'Contact Owner.id': string;
}

interface ZohoDeal {
  'Record Id': string;
  'Deal Name': string;
  'Amount': string;
  'Total': string;
  'Payment Total': string;
  'Closing Date': string;
  'Stage': string;
  'Contact Name.id': string;
  'Deal Owner.id': string;
  'Created Time': string;
  'Lead Source': string;
}

interface ZohoLead {
  'Record Id': string;
  'First Name': string;
  'Last Name': string;
  'Email': string;
  'Mobile': string;
  'Is Converted': string;
  'Lead Status': string;
  'Not Converted Reason': string;
  'Lead Source': string;
  'Created Time': string;
  'Lead Owner.id': string;
  'Converted Contact.id': string;
}

interface ZohoCall {
  'Record Id': string;
  'Call Type': string;
  'Call Start Time': string;
  'Call Duration': string;
  'From Number': string;
  'To Number': string;
  'Related To.id': string;
  'Created By.id': string;
  'Subject': string;
  'Description': string;
}

interface ZohoItem {
  'Record Id': string;
  'Case Information Owner.id': string;
  'Case Information Owner': string;
  'Parent ID.id': string;
  'Parent ID': string;
  'Applicant': string;
  'Case Type': string;
  'Amountt': string;
  'Dependents': string;
  'Created Time': string;
  'Modified Time': string;
}

interface ZohoPayment {
  'Record Id': string;
  'Payment Info Owner.id': string;
  'Payment Info Owner': string;
  'Parent ID.id': string;
  'Parent ID': string;
  'Amount': string;
  'Start/Payment Date': string;
  'Payment Method': string;
  'Payment Type': string;
  'Number of Payments': string;
  'Frequency': string;
  'Payment Total': string;
  'Created Time': string;
  'Modified Time': string;
}

interface ZohoNote {
  'Record Id': string;
  'Parent ID.id': string;
  'Note Content': string;
  'Note Title': string;
  'Created By.id': string;
  'Created By': string;
  'Created Time': string;
  'Modified By.id': string;
  'Modified By': string;
  'Modified Time': string;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

function parseZohoDate(dateStr: string): string | null {
  if (!dateStr || dateStr.trim() === '') return null;
  
  try {
    // Formato: "2025-07-07 11:15:21"
    const [datePart] = dateStr.split(' ');
    return datePart; // Retorna apenas "2025-07-07"
  } catch {
    return null;
  }
}

function parseAmount(amountStr: string): number {
  if (!amountStr || amountStr.trim() === '') return 0;
  const cleaned = amountStr.replace(/[^0-9.-]/g, '');
  const value = parseFloat(cleaned);
  return isNaN(value) ? 0 : value;
}

function parseCallDuration(durationStr: string): number {
  if (!durationStr) return 0;
  // Formato pode ser "00:05:30" ou "330" (segundos)
  if (durationStr.includes(':')) {
    const parts = durationStr.split(':');
    const hours = parseInt(parts[0] || '0');
    const minutes = parseInt(parts[1] || '0');
    const seconds = parseInt(parts[2] || '0');
    return hours * 3600 + minutes * 60 + seconds;
  }
  return parseInt(durationStr) || 0;
}

function normalizePhone(phone: string): string {
  if (!phone) return '';
  // Converter para string se não for
  const phoneStr = String(phone);
  // Remove tudo exceto números e o símbolo +
  return phoneStr.replace(/[^0-9+]/g, '');
}

function stripHtmlTags(html: string): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim();
}

// ============================================
// IMPORTAÇÃO DE USERS
// ============================================

export async function importUsers(users: ZohoUser[]) {
  console.log(`🔄 Iniciando importação de ${users.length} usuários...`);
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [] as string[],
  };

  // 1. Buscar users existentes
  const { data: existingUsers } = await supabase
    .from('app_users')
    .select('id, email, zoho_user_id');

  const userMap = new Map<string, any>();
  existingUsers?.forEach(u => {
    if (u.zoho_user_id) userMap.set(u.zoho_user_id, u);
    if (u.email) userMap.set(u.email, u);
  });

  for (const zohoUser of users) {
    try {
      // Pular usuários inativos/deletados
      if (zohoUser.Status === 'Inactive' || zohoUser.Status === 'deleted') {
        results.skipped++;
        continue;
      }

      const zohoUserId = zohoUser['Record Id'];
      const email = zohoUser.Email;
      const roleStr = zohoUser.Role || 'Standard';
      
      // Buscar system_role_id
      const { data: systemRole } = await supabase
        .from('system_roles')
        .select('id')
        .eq('name', ROLE_MAPPING[roleStr] || 'standard')
        .single();

      const userData = {
        first_name: zohoUser['First Name'],
        last_name: zohoUser['Last Name'],
        email: email,
        display_name: zohoUser['Display Name'] || `${zohoUser['First Name']} ${zohoUser['Last Name']}`,
        active: zohoUser.Status === 'Active',
        zoho_user_id: zohoUserId,
        system_role_id: systemRole?.id,
        metadata: {
          zoho_role: zohoUser.Role,
          imported_at: new Date().toISOString(),
        },
      };

      // Verificar se user existe
      const existingUser = userMap.get(zohoUserId) || userMap.get(email);

      if (existingUser) {
        // UPDATE
        const { error } = await supabase
          .from('app_users')
          .update({
            zoho_user_id: userData.zoho_user_id,
            system_role_id: userData.system_role_id,
            display_name: userData.display_name,
            metadata: userData.metadata,
          })
          .eq('id', existingUser.id);

        if (error) {
          results.errors.push(`Erro ao atualizar user ${email}: ${error.message}`);
        } else {
          results.updated++;
        }
      } else {
        // CREATE
        const { error } = await supabase
          .from('app_users')
          .insert(userData);

        if (error) {
          results.errors.push(`Erro ao criar user ${email}: ${error.message}`);
        } else {
          results.created++;
        }
      }
    } catch (err: any) {
      results.errors.push(`Erro ao processar user ${zohoUser.Email}: ${err.message}`);
    }
  }

  console.log('✅ Importação de users concluída:', results);
  return results;
}

// ============================================
// IMPORTAÇÃO DE CONTACTS
// ============================================

export async function importContacts(contacts: ZohoContact[]) {
  console.log(`🔄 Iniciando importação de ${contacts.length} contatos...`);
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [] as string[],
  };

  // 1. Buscar contacts existentes
  const { data: existingContacts } = await supabase
    .from('contacts')
    .select('id, primary_email, zoho_contact_id');

  const contactMap = new Map<string, any>();
  existingContacts?.forEach(c => {
    if (c.zoho_contact_id) contactMap.set(c.zoho_contact_id, c);
    if (c.primary_email) contactMap.set(c.primary_email, c);
  });

  // 2. Buscar app_users para matching
  const { data: appUsers } = await supabase
    .from('app_users')
    .select('id, zoho_user_id');
  
  const userMap = new Map<string, string>();
  appUsers?.forEach(u => {
    if (u.zoho_user_id) userMap.set(u.zoho_user_id, u.id);
  });

  // Preparar registros para operações em lote
  const toInsert: any[] = [];
  const toUpsertByZohoId: any[] = [];
  const toUpdateById: Array<{ id: string; data: any; email: string | null }> = [];

  for (const zohoContact of contacts) {
    const zohoContactId = zohoContact['Record Id'];
    const email = zohoContact.Email || null;
    const createdByZohoId = zohoContact['Contact Owner.id'];
    const createdByUserId = userMap.get(createdByZohoId) || null;

    const base = {
      first_name: zohoContact['First Name'] || null,
      last_name: zohoContact['Last Name'] || null,
      primary_email: email,
      primary_phone: normalizePhone(zohoContact.Mobile) || null,
      date_of_birth: parseZohoDate(zohoContact['Date of Birth']),
      created_by: createdByUserId,
      zoho_contact_id: zohoContactId,
      created_at: parseZohoDate(zohoContact['Created Time']) || new Date().toISOString(),
      metadata: {
        lead_source: zohoContact['Lead Source'],
        imported_at: new Date().toISOString(),
      },
    };

    const byZoho = contactMap.get(zohoContactId);
    const byEmail = email ? contactMap.get(email) : null;

    if (byZoho) {
      toUpsertByZohoId.push(base);
    } else if (byEmail) {
      // Atualiza o registro existente (sem upsert para evitar duplicar)
      toUpdateById.push({ id: byEmail.id, data: base, email });
    } else {
      toInsert.push(base);
    }
  }

  const chunk = <T,>(arr: T[], size = 500) =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) => arr.slice(i * size, (i + 1) * size));

  // Inserções em lote
  for (const part of chunk(toInsert, 500)) {
    const { error } = await supabase.from('contacts').insert(part);
    if (error) results.errors.push(`Erro ao inserir contatos em lote: ${error.message}`);
  }
  results.created += toInsert.length;

  // Upsert por zoho_contact_id em lote
  for (const part of chunk(toUpsertByZohoId, 500)) {
    const { error } = await supabase
      .from('contacts')
      .upsert(part, { onConflict: 'zoho_contact_id' });
    if (error) results.errors.push(`Erro ao atualizar (upsert) contatos em lote: ${error.message}`);
  }
  results.updated += toUpsertByZohoId.length;

  // Updates por id (quando só encontramos por email)
  const updateChunks = chunk(toUpdateById, 25);
  for (const part of updateChunks) {
    await Promise.all(
      part.map(async ({ id, data, email }) => {
        const { error } = await supabase
          .from('contacts')
          .update({
            first_name: data.first_name,
            last_name: data.last_name,
            primary_email: data.primary_email,
            primary_phone: data.primary_phone,
            date_of_birth: data.date_of_birth,
            metadata: data.metadata,
            zoho_contact_id: data.zoho_contact_id,
          })
          .eq('id', id);
        if (error) results.errors.push(`Erro ao atualizar contact ${email || ''}: ${error.message}`);
      })
    );
  }
  results.updated += toUpdateById.length;


  console.log('✅ Importação de contacts concluída:', results);
  return results;
}

// ============================================
// IMPORTAÇÃO DE DEALS
// ============================================

export async function importDeals(deals: ZohoDeal[]) {
  console.log(`🔄 Iniciando importação de ${deals.length} deals...`);
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    notesCreated: 0,
    errors: [] as string[],
  };

  // 1. Buscar deals existentes
  const { data: existingDeals } = await supabase
    .from('deals')
    .select('id, zoho_deal_id');

  const dealMap = new Map<string, any>();
  existingDeals?.forEach(d => {
    if (d.zoho_deal_id) dealMap.set(d.zoho_deal_id, d);
  });
  
  console.log(`📊 Deals existentes: ${dealMap.size}`);

  // 2. Buscar contacts para matching
  const { data: contacts } = await supabase
    .from('contacts')
    .select('id, zoho_contact_id');
  
  const contactMap = new Map<string, string>();
  contacts?.forEach(c => {
    if (c.zoho_contact_id) contactMap.set(c.zoho_contact_id, c.id);
  });
  
  console.log(`👥 Contacts mapeados: ${contactMap.size}`);

  // 3. Buscar app_users para matching
  const { data: appUsers } = await supabase
    .from('app_users')
    .select('id, zoho_user_id');
  
  const userMap = new Map<string, string>();
  appUsers?.forEach(u => {
    if (u.zoho_user_id) userMap.set(u.zoho_user_id, u.id);
  });
  
  console.log(`🧑‍💼 Users mapeados: ${userMap.size}`);

  let processedCount = 0;
  const totalDeals = deals.length;

  for (const zohoDeal of deals) {
    try {
      processedCount++;
      
      // Log a cada 100 deals
      if (processedCount % 100 === 0) {
        console.log(`⏳ Processando ${processedCount}/${totalDeals} deals (${Math.round(processedCount/totalDeals*100)}%)`);
      }
      
      const zohoDealId = zohoDeal['Record Id'];
      
      // CRÍTICO: Usar Amount como prioridade
      const amount = parseAmount(zohoDeal.Amount);
      const paymentTotal = parseAmount(zohoDeal['Payment Total']);
      
      // Closing Date pode ser null
      const closingDate = parseZohoDate(zohoDeal['Closing Date']);
      
      // Stage mapping
      const stage = DEAL_STAGE_MAPPING[zohoDeal.Stage] || 'NEW';
      
      // Buscar contact_id
      const contactZohoId = zohoDeal['Contact Name.id'];
      let contactId = contactMap.get(contactZohoId) || null;
      
      // Se não encontrar contact, criar/buscar um placeholder
      if (!contactId && contactZohoId) {
        let contactName = zohoDeal['Contact Name'] || 'Unknown Contact';
        
        // Remove " - deal" do nome se estiver presente
        if (contactName.toLowerCase().endsWith(' - deal')) {
          contactName = contactName.slice(0, -7).trim();
        }
        
        const [firstName, ...lastNameParts] = contactName.split(' ');
        
        // Tentar inserir com upsert
        const { data: newContact, error: contactError } = await supabase
          .from('contacts')
          .upsert({
            first_name: firstName,
            last_name: lastNameParts.join(' ') || 'Unknown',
            zoho_contact_id: contactZohoId,
            metadata: { imported_as_placeholder: true, from_deal: zohoDeal['Deal Name'] }
          }, {
            onConflict: 'zoho_contact_id',
            ignoreDuplicates: false
          })
          .select('id')
          .maybeSingle();
        
        if (!contactError && newContact) {
          contactId = newContact.id;
          contactMap.set(contactZohoId, newContact.id);
        } else if (contactError) {
          // Se falhar, tentar buscar o existente
          const { data: existingContact } = await supabase
            .from('contacts')
            .select('id')
            .eq('zoho_contact_id', contactZohoId)
            .maybeSingle();
          
          if (existingContact) {
            contactId = existingContact.id;
            contactMap.set(contactZohoId, existingContact.id);
          }
        }
      }
      
      // Fallback: se ainda sem contactId, inferir do Deal Name/Contact Name
      if (!contactId) {
        let inferredName = (zohoDeal['Contact Name'] || '').trim();
        if (!inferredName) {
          const dealName = (zohoDeal['Deal Name'] || '').trim();
          if (dealName) {
            // Usa parte antes de " - " se existir
            inferredName = dealName.split(' - ')[0].trim();
          }
        }
        if (inferredName) {
          // Remove sufixo "- deal" se houver
          if (inferredName.toLowerCase().endsWith(' - deal')) {
            inferredName = inferredName.slice(0, -7).trim();
          }
          const [fn, ...lnParts] = inferredName.split(' ');
          const { data: inferredContact } = await supabase
            .from('contacts')
            .insert({
              first_name: fn || 'Unknown',
              last_name: lnParts.join(' ') || 'Unknown',
              metadata: { imported_as_placeholder: true, inferred_from: 'deal', deal_record_id: zohoDealId }
            })
            .select('id')
            .maybeSingle();
          if (inferredContact) {
            contactId = inferredContact.id;
          }
        }
      }
      
      // Buscar owner_id
      const ownerZohoId = zohoDeal['Deal Owner.id'];
      let ownerId = userMap.get(ownerZohoId) || null;
      
      // Se não encontrar owner, criar/buscar um placeholder
      if (!ownerId && ownerZohoId) {
        const ownerName = zohoDeal['Deal Owner'] || 'Unknown User';
        const [firstName, ...lastNameParts] = ownerName.split(' ');
        
        // Tentar inserir com upsert
        const { data: newUser, error: userError } = await supabase
          .from('app_users')
          .upsert({
            first_name: firstName,
            last_name: lastNameParts.join(' ') || 'Unknown',
            zoho_user_id: ownerZohoId,
            email: `placeholder_${ownerZohoId}@zoho.import`,
            active: false,
            metadata: { imported_as_placeholder: true }
          }, {
            onConflict: 'zoho_user_id',
            ignoreDuplicates: false
          })
          .select('id')
          .maybeSingle();
        
        if (!userError && newUser) {
          ownerId = newUser.id;
          userMap.set(ownerZohoId, newUser.id);
        } else if (userError) {
          // Se falhar, tentar buscar o existente
          const { data: existingUser } = await supabase
            .from('app_users')
            .select('id')
            .eq('zoho_user_id', ownerZohoId)
            .maybeSingle();
          
          if (existingUser) {
            ownerId = existingUser.id;
            userMap.set(ownerZohoId, existingUser.id);
          }
        }
      }

      if (!contactId) {
        results.errors.push(`Deal ${zohoDeal['Deal Name']}: contact not found (${contactZohoId})`);
        results.skipped++;
        continue;
      }

      if (!ownerId) {
        results.errors.push(`Deal ${zohoDeal['Deal Name']}: owner not found (${ownerZohoId})`);
        results.skipped++;
        continue;
      }

      const dealData = {
        name: zohoDeal['Deal Name'] || (zohoDeal['Contact Name'] ? `${zohoDeal['Contact Name']} - deal` : 'Unnamed Deal'),
        total_amount: amount,
        stage: stage as any,
        contact_id: contactId,
        sales_consultant: ownerId,
        source: 'pipeline' as any,
        expected_close_date: closingDate,
        closed_date: stage === 'won' || stage === 'lost' ? closingDate : null,
        zoho_deal_id: zohoDealId,
        created_at: parseZohoDate(zohoDeal['Created Time']) || new Date().toISOString(),
        metadata: {
          lead_source: zohoDeal['Lead Source'],
          payment_total: paymentTotal,
          imported_at: new Date().toISOString(),
        },
      };

      // Verificar se deal existe
      const existingDeal = dealMap.get(zohoDealId);

      let dealId: string;

      if (existingDeal) {
        // UPDATE
        const { error } = await supabase
          .from('deals')
          .update({
            name: dealData.name,
            total_amount: dealData.total_amount,
            stage: dealData.stage,
            contact_id: dealData.contact_id,
            expected_close_date: dealData.expected_close_date,
            metadata: dealData.metadata,
          })
          .eq('id', existingDeal.id);

        if (error) {
          results.errors.push(`Erro ao atualizar deal ${dealData.name}: ${error.message}`);
          continue;
        }
        
        dealId = existingDeal.id;
        results.updated++;
      } else {
        // CREATE
        const { data: newDeal, error } = await supabase
          .from('deals')
          .insert(dealData)
          .select('id')
          .maybeSingle();

        if (error) {
          results.errors.push(`Erro ao criar deal ${dealData.name}: ${error.message}`);
          results.skipped++;
          continue;
        }
        
        if (!newDeal) {
          results.errors.push(`Erro ao criar deal ${dealData.name}: sem retorno`);
          results.skipped++;
          continue;
        }
        
        dealId = newDeal.id;
        results.created++;
      }

    } catch (err: any) {
      results.errors.push(`Erro ao processar deal ${zohoDeal['Deal Name'] || zohoDeal['Record Id']}: ${err.message}`);
      results.skipped++;
      console.error(`❌ Erro no deal ${zohoDeal['Deal Name']}:`, err);
    }
  }

  console.log('✅ Importação de deals concluída:', results);
  console.log(`📊 Final: ${results.created} criados, ${results.updated} atualizados, ${results.skipped} pulados`);
  if (results.errors.length > 0) {
    console.log(`⚠️ Erros (${results.errors.length}):`, results.errors.slice(0, 10));
  }
  return results;
}

// ============================================
// IMPORTAÇÃO DE LEADS
// ============================================

export async function importLeads(leads: ZohoLead[]) {
  console.log(`🔄 Iniciando importação de ${leads.length} leads...`);
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [] as string[],
  };

  // 1. Buscar leads existentes
  const { data: existingLeads } = await supabase
    .from('leads')
    .select('id, zoho_lead_id');

  const leadMap = new Map<string, any>();
  existingLeads?.forEach(l => {
    if (l.zoho_lead_id) leadMap.set(l.zoho_lead_id, l);
  });

  // 2. Buscar app_users para matching
  const { data: appUsers } = await supabase
    .from('app_users')
    .select('id, zoho_user_id');
  
  const userMap = new Map<string, string>();
  appUsers?.forEach(u => {
    if (u.zoho_user_id) userMap.set(u.zoho_user_id, u.id);
  });

  // 3. Buscar contacts para matching converted leads
  const { data: contacts } = await supabase
    .from('contacts')
    .select('id, zoho_contact_id');
  
  const contactMap = new Map<string, string>();
  contacts?.forEach(c => {
    if (c.zoho_contact_id) contactMap.set(c.zoho_contact_id, c.id);
  });

  for (const zohoLead of leads) {
    try {
      const zohoLeadId = zohoLead['Record Id'];
      const email = zohoLead.Email;
      const isConverted = zohoLead['Is Converted'] === 'true';
      
      // Buscar owner_id
      const ownerZohoId = zohoLead['Lead Owner.id'];
      const ownerId = userMap.get(ownerZohoId) || null;
      
      // Buscar converted_contact_id
      const convertedContactZohoId = zohoLead['Converted Contact.id'];
      const convertedContactId = convertedContactZohoId ? contactMap.get(convertedContactZohoId) : null;

      // Not converted reason pode ser vazio se Is Converted = false
      const notConvertedReason = !isConverted ? (zohoLead['Not Converted Reason'] || null) : null;

      const leadData = {
        first_name: zohoLead['First Name'],
        last_name: zohoLead['Last Name'],
        primary_email: email,
        primary_phone: normalizePhone(zohoLead.Mobile),
        market: 'br',
        origem: zohoLead['Lead Source'] || 'zoho',
        assigned_to: ownerId,
        contact_id: convertedContactId,
        zoho_lead_id: zohoLeadId,
        created_at: parseZohoDate(zohoLead['Created Time']) || new Date().toISOString(),
        metadata: {
          lead_status: zohoLead['Lead Status'],
          is_converted: isConverted,
          not_converted_reason: notConvertedReason,
          imported_at: new Date().toISOString(),
        },
      };

      // Verificar se lead existe
      const existingLead = leadMap.get(zohoLeadId);

      if (existingLead) {
        // UPDATE
        const { error } = await supabase
          .from('leads')
          .update({
            first_name: leadData.first_name,
            last_name: leadData.last_name,
            primary_email: leadData.primary_email,
            primary_phone: leadData.primary_phone,
            contact_id: leadData.contact_id,
            metadata: leadData.metadata,
          })
          .eq('id', existingLead.id);

        if (error) {
          results.errors.push(`Erro ao atualizar lead ${email}: ${error.message}`);
        } else {
          results.updated++;
        }
      } else {
        // CREATE
        const { error } = await supabase
          .from('leads')
          .insert([leadData]);

        if (error) {
          results.errors.push(`Erro ao criar lead ${email}: ${error.message}`);
        } else {
          results.created++;
        }
      }
    } catch (err: any) {
      results.errors.push(`Erro ao processar lead ${zohoLead.Email}: ${err.message}`);
    }
  }

  console.log('✅ Importação de leads concluída:', results);
  return results;
}

// ============================================
// IMPORTAÇÃO DE CALLS
// ============================================

export async function importCalls(calls: ZohoCall[]) {
  console.log(`🔄 Iniciando importação de ${calls.length} chamadas...`);
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [] as string[],
  };

  // 1. Buscar calls existentes
  const { data: existingCalls } = await supabase
    .from('calls')
    .select('id, zoho_call_id');

  const callMap = new Map<string, any>();
  existingCalls?.forEach(c => {
    if (c.zoho_call_id) callMap.set(c.zoho_call_id, c);
  });

  // 2. Buscar contacts para matching
  const { data: contacts } = await supabase
    .from('contacts')
    .select('id, primary_phone, zoho_contact_id');
  
  const contactByZohoIdMap = new Map<string, string>();
  const contactByPhoneMap = new Map<string, string>();
  contacts?.forEach(c => {
    if (c.zoho_contact_id) contactByZohoIdMap.set(c.zoho_contact_id, c.id);
    if (c.primary_phone) contactByPhoneMap.set(normalizePhone(c.primary_phone), c.id);
  });

  // 3. Buscar app_users para matching
  const { data: appUsers } = await supabase
    .from('app_users')
    .select('id, zoho_user_id');
  
  const userMap = new Map<string, string>();
  appUsers?.forEach(u => {
    if (u.zoho_user_id) userMap.set(u.zoho_user_id, u.id);
  });

  for (const zohoCall of calls) {
    try {
      const zohoCallId = zohoCall['Record Id'];
      
      // Buscar contact_id - primeiro por Related To, depois por telefone
      let contactId: string | null = null;
      
      const relatedToZohoId = zohoCall['Related To.id'];
      if (relatedToZohoId) {
        contactId = contactByZohoIdMap.get(relatedToZohoId) || null;
      }
      
      // 🎯 FALLBACK: Se não achou por Related To, buscar por telefone
      if (!contactId) {
        const fromNumber = normalizePhone(zohoCall['From Number']);
        const toNumber = normalizePhone(zohoCall['To Number']);
        
        contactId = contactByPhoneMap.get(fromNumber) || contactByPhoneMap.get(toNumber) || null;
      }
      
      // Buscar created_by user_id
      const createdByZohoId = zohoCall['Created By.id'];
      const createdByUserId = userMap.get(createdByZohoId) || null;
      
      const duration = parseCallDuration(zohoCall['Call Duration']);
      const startTime = parseZohoDate(zohoCall['Call Start Time']);

      const callData = {
        contact_id: contactId,
        direction: (zohoCall['Call Type'] === 'Outbound' ? 'outbound' : 'inbound') as 'outbound' | 'inbound',
        from_e164: normalizePhone(zohoCall['From Number']),
        to_e164: normalizePhone(zohoCall['To Number']),
        duration_seconds: duration,
        status: duration > 0 ? 'completed' : 'no-answer',
        started_at: startTime || new Date().toISOString(),
        created_by: createdByUserId,
        zoho_call_id: zohoCallId,
        channel: 'voice' as 'voice',
        metadata: {
          subject: zohoCall.Subject,
          description: zohoCall.Description,
          imported_at: new Date().toISOString(),
        },
      };

      // Verificar se call existe
      const existingCall = callMap.get(zohoCallId);

      if (existingCall) {
        // UPDATE
        const { error } = await supabase
          .from('calls')
          .update({
            contact_id: callData.contact_id,
            direction: callData.direction,
            from_e164: callData.from_e164,
            to_e164: callData.to_e164,
            duration_seconds: callData.duration_seconds,
            status: callData.status,
            started_at: callData.started_at,
            metadata: callData.metadata,
          })
          .eq('id', existingCall.id);

        if (error) {
          results.errors.push(`Erro ao atualizar call ${zohoCallId}: ${error.message}`);
        } else {
          results.updated++;
        }
      } else {
        // CREATE
        const { error } = await supabase
          .from('calls')
          .insert([callData]);

        if (error) {
          results.errors.push(`Erro ao criar call ${zohoCallId}: ${error.message}`);
        } else {
          results.created++;
        }
      }
    } catch (err: any) {
      results.errors.push(`Erro ao processar call ${zohoCall['Record Id']}: ${err.message}`);
    }
  }

  console.log('✅ Importação de calls concluída:', results);
  return results;
}

// ============================================
// IMPORTAÇÃO DE ITEMS
// ============================================

export async function importItems(items: ZohoItem[]) {
  console.log(`🔄 Iniciando importação de ${items.length} items...`);
  console.log('📋 Primeiras 3 linhas do CSV:', items.slice(0, 3));
  
  // Função auxiliar para normalizar strings
  const normalizeString = (str: string): string => {
    return str
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[-_]/g, ' ') // Substitui hífens e underscores por espaço
      .replace(/\s+/g, ' ') // Colapsa múltiplos espaços
      .trim();
  };
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [] as string[],
    deals_missing: 0,
    catalogs_matched: 0,
    catalogs_created: 0,
  };

  // 1. Buscar deals para matching (incluindo stage para definir status correto)
  const { data: deals } = await supabase
    .from('deals')
    .select('id, zoho_deal_id, stage');
  
  const dealMap = new Map<string, { id: string; stage: string }>();
  deals?.forEach(d => {
    if (d.zoho_deal_id) {
      const normalizedKey = (d.zoho_deal_id || '').trim().toLowerCase();
      dealMap.set(normalizedKey, { id: d.id, stage: d.stage });
    }
  });
  
  console.log(`📊 Deals mapeados: ${dealMap.size}`);
  console.log('🔍 Primeiros 5 zoho_deal_ids:', Array.from(dealMap.keys()).slice(0, 5));

  // Preflight check: verificar quantos Parent IDs não têm deal correspondente (após normalização)
  const parentIds = Array.from(new Set(
    items.map(i => (i['Parent ID.id'] || '').trim().toLowerCase()).filter(Boolean)
  ));
  const missingParentIds = parentIds.filter(id => !dealMap.has(id));
  console.log(`❗ Parent deals não encontrados: ${missingParentIds.length} de ${parentIds.length}`);
  if (missingParentIds.length > 0) {
    console.log('🔍 Sample de Parent IDs ausentes:', missingParentIds.slice(0, 20));
    console.log('💡 Dica: Reimporte o Step 2 - Deals com o CSV contendo esses IDs antes de continuar.');
  }
  results.deals_missing = missingParentIds.length;

  // 2. Buscar catalog items para matching
  const { data: catalogItems } = await supabase
    .from('catalog')
    .select('*');
  
  const catalogMapByName = new Map<string, string>();
  const catalogMapByNormalized = new Map<string, string>();
  let fallbackCatalogId: string | null = null;
  
  catalogItems?.forEach(c => {
    if (c.name) {
      catalogMapByName.set(c.name.toLowerCase().trim(), c.id);
      catalogMapByNormalized.set(normalizeString(c.name), c.id);
      
      // Usar primeiro catálogo como fallback
      if (!fallbackCatalogId) {
        fallbackCatalogId = c.id;
      }
    }
  });
  
  // Dicionário de sinônimos (normalizado -> nome de catálogo)
  const catalogSynonyms: Record<string, string> = {
    'eb2 niw': 'EB2-NIW',
    'eb 2 niw': 'EB2-NIW',
    'eb2niw': 'EB2-NIW',
    'u visa': 'U-Visa',
    'uvisa': 'U-Visa',
    'perm': 'PERM Labor Certification',
    'perm labor certification': 'PERM Labor Certification',
    'sijs': 'SIJS',
    'consultoria inicial': 'Consultoria Inicial',
    'initial consultation': 'Consultoria Inicial',
    'consultation': 'Consultoria Inicial',
  };
  
  console.log(`📦 Catalog items mapeados: ${catalogMapByName.size}`);
  console.log(`🔄 Fallback catalog ID: ${fallbackCatalogId}`);

  for (const zohoItem of items) {
    try {
      const parentDealZohoId = zohoItem['Parent ID.id'];
      
      console.log(`🔍 Processando item: Parent ID.id = "${parentDealZohoId}", Case Type = "${zohoItem['Case Type']}", Amount = "${zohoItem['Amountt']}"`);
      
      // Skip se não tem deal parent
      if (!parentDealZohoId) {
        console.log('⚠️ Item pulado: sem Parent ID.id');
        results.skipped++;
        continue;
      }
      
      // Buscar deal (id + stage) com normalização
      const normalizedParentId = (parentDealZohoId || '').trim().toLowerCase();
      const deal = dealMap.get(normalizedParentId);
      console.log(`🔍 Deal matching: zoho_id="${parentDealZohoId}" (normalized="${normalizedParentId}") -> deal_id="${deal?.id}"`);
      
      if (!deal) {
        results.errors.push(`Item ${zohoItem['Record Id']}: Deal não encontrado (${parentDealZohoId})`);
        results.skipped++;
        continue;
      }
      
      const dealId = deal.id;
      
      // Buscar catalog_id baseado no Case Type com normalização e sinônimos
      let catalogId: string | null = null;
      const caseTypeRaw = zohoItem['Case Type'] || '';
      
      if (caseTypeRaw) {
        const normalizedCaseType = normalizeString(caseTypeRaw);
        
        // Tentar sinônimo primeiro
        let targetName = catalogSynonyms[normalizedCaseType] || caseTypeRaw;
        
        // Buscar no mapa normalizado
        catalogId = catalogMapByNormalized.get(normalizeString(targetName)) || null;
        
        // Se não encontrou, tentar pelo nome original
        if (!catalogId) {
          catalogId = catalogMapByName.get(targetName.toLowerCase().trim()) || null;
        }
        
        // Se ainda não encontrou, criar catálogo automaticamente
        if (!catalogId) {
          console.log(`🆕 Tentando criar catálogo: "${caseTypeRaw.trim()}"`);
          
          const unitPrice = parseAmount(zohoItem['Amountt'] || '0');
          const numDependents = parseInt(zohoItem['Dependents'] || '0');
          
          try {
            const { data: newCatalog, error: catalogError } = await supabase
              .from('catalog')
              .insert({
                name: caseTypeRaw.trim(),
                type: 'case',
                pricing_type: 'flat',
                base_price: unitPrice,
                allows_dependents: numDependents > 0,
                dependent_price: null,
                has_cost: false,
                cost_type: 'flat',
                is_active: true,
                primary_person_label: 'Applicant',
                secondary_person_label: null,
                has_secondary_person: false,
                dependent_label: 'Dependent',
              })
              .select()
              .single();
            
            if (catalogError) {
              console.error(`❌ Erro ao criar catálogo "${caseTypeRaw.trim()}":`, catalogError.message, catalogError.details);
              
              // FALLBACK: usar catálogo genérico se disponível
              if (fallbackCatalogId) {
                console.log(`🔄 Usando catálogo fallback (${fallbackCatalogId}) para "${caseTypeRaw.trim()}"`);
                catalogId = fallbackCatalogId;
              } else {
                results.errors.push(`Catálogo não criado e sem fallback: ${caseTypeRaw.trim()}`);
              }
            } else if (newCatalog) {
              catalogId = newCatalog.id;
              // Atualizar os mapas
              catalogMapByName.set(caseTypeRaw.toLowerCase().trim(), newCatalog.id);
              catalogMapByNormalized.set(normalizeString(caseTypeRaw), newCatalog.id);
              results.catalogs_created++;
              console.log(`✅ Catálogo criado: "${caseTypeRaw.trim()}" (id: ${newCatalog.id})`);
            }
          } catch (createError: any) {
            console.error(`❌ Exceção ao criar catálogo:`, createError);
            
            // FALLBACK em caso de exceção
            if (fallbackCatalogId) {
              console.log(`🔄 Usando catálogo fallback devido à exceção`);
              catalogId = fallbackCatalogId;
            }
          }
        } else {
          results.catalogs_matched++;
        }
      }
      
      // Parse valores
      const unitPrice = parseAmount(zohoItem['Amountt'] || '0');
      const numDependents = parseInt(zohoItem['Dependents'] || '0');
      
      // Skip se não tem valor ou catalog_id
      if (unitPrice === 0 || !catalogId) {
        results.skipped++;
        if (!catalogId) {
          results.errors.push(`Item ${zohoItem['Record Id']}: catalog_id null após tentativa de criação`);
        }
        continue;
      }
      
      // ✅ NÃO calcular subtotal, dependent_total e total_amount manualmente
      // Essas colunas são GENERATED COLUMNS no banco e são calculadas automaticamente
      
      // ✅ Definir status baseado no stage do deal
      const itemStatus = deal.stage === 'closed_won' ? 'sold' : 'proposed';
      
      const itemData = {
        deal_id: dealId,
        catalog_id: catalogId,
        unit_price: unitPrice,
        num_dependents: numDependents,
        quantity: 1,
        status: itemStatus as any,
        notes: zohoItem['Applicant'] ? `Applicant: ${zohoItem['Applicant']}` : null,
      };
      
      // Verificar se item já existe (pelo deal_id e unit_price)
      const { data: existing } = await supabase
        .from('items')
        .select('id')
        .eq('deal_id', dealId)
        .eq('unit_price', unitPrice)
        .maybeSingle();
      
      if (existing) {
        // UPDATE
        const { error } = await supabase
          .from('items')
          .update({
            catalog_id: itemData.catalog_id,
            num_dependents: itemData.num_dependents,
            status: itemStatus as any,
            notes: itemData.notes,
          })
          .eq('id', existing.id);
        
        if (error) {
          results.errors.push(`Erro ao atualizar item: ${error.message}`);
        } else {
          results.updated++;
        }
      } else {
        // CREATE
        const { error } = await supabase
          .from('items')
          .insert(itemData);
        
        if (error) {
          results.errors.push(`Erro ao criar item: ${error.message}`);
        } else {
          results.created++;
        }
      }
      
      // Atualizar notes do deal com os items
      await updateDealNotesWithItems(dealId);
      
    } catch (err: any) {
      results.errors.push(`Erro ao processar item ${zohoItem['Record Id']}: ${err.message}`);
    }
  }

  console.log('✅ Importação de items concluída:', results);
  return results;
}

// Helper para atualizar notes do deal com items
async function updateDealNotesWithItems(dealId: string) {
  // Buscar todos os items do deal
  const { data: items } = await supabase
    .from('items')
    .select('*, catalog(*)')
    .eq('deal_id', dealId);
  
  if (!items || items.length === 0) return;
  
  // Buscar deal atual
  const { data: deal } = await supabase
    .from('deals')
    .select('notes')
    .eq('id', dealId)
    .single();
  
  if (!deal) return;
  
  // Construir seção de items em formato markdown bonito
  let itemsSection = '## 📋 Services/Items\n\n';
  
  items.forEach((item, index) => {
    const catalogName = item.catalog?.name || 'Unknown Service';
    itemsSection += `### ${index + 1}. ${catalogName}\n`;
    itemsSection += `- **Amount:** $${item.unit_price?.toLocaleString() || 0}\n`;
    if (item.num_dependents > 0) {
      itemsSection += `- **Dependents:** ${item.num_dependents}\n`;
    }
    if (item.notes) {
      itemsSection += `- **Note:** ${item.notes}\n`;
    }
    itemsSection += '\n';
  });
  
  const totalServices = items.reduce((sum, item) => sum + (item.unit_price || 0), 0);
  itemsSection += `**Total Services:** $${totalServices.toLocaleString()}\n\n`;
  
  // Manter notes existentes e adicionar items
  let existingNotes = deal.notes || '';
  
  // Remover seção antiga de items se existir
  existingNotes = existingNotes.replace(/## 📋 Services\/Items[\s\S]*?(?=\n\n##|\n\n---\n|$)/g, '');
  existingNotes = existingNotes.replace(/\n\n=== SERVICES\/ITEMS ===[\s\S]*?(?=\n\n===|$)/g, '');
  
  const updatedNotes = existingNotes + (existingNotes ? '\n\n' : '') + itemsSection;
  
  // Atualizar deal
  await supabase
    .from('deals')
    .update({ notes: updatedNotes.trim() })
    .eq('id', dealId);
}

// ============================================
// IMPORTAÇÃO DE PAYMENTS
// ============================================

export async function importPayments(payments: ZohoPayment[]) {
  console.log(`🔄 Iniciando importação de ${payments.length} payments...`);
  console.log('💰 Primeiras 3 linhas do CSV:', payments.slice(0, 3));
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [] as string[],
  };

  // 1. Buscar deals para matching
  const { data: deals } = await supabase
    .from('deals')
    .select('id, zoho_deal_id, metadata');
  
  const dealMap = new Map<string, any>();
  deals?.forEach(d => {
    if (d.zoho_deal_id) dealMap.set(d.zoho_deal_id, d);
  });
  
  console.log(`📊 Deals mapeados: ${dealMap.size}`);
  console.log('🔍 Primeiros 5 zoho_deal_ids:', Array.from(dealMap.keys()).slice(0, 5));

  for (const zohoPayment of payments) {
    try {
      const parentDealZohoId = zohoPayment['Parent ID.id'];
      
      console.log(`💰 Processando payment: Parent ID.id = "${parentDealZohoId}", Amount = "${zohoPayment['Amount']}"`);
      
      // Skip se não tem deal parent ou não tem valor
      if (!parentDealZohoId || !zohoPayment['Amount']) {
        console.log('⚠️ Payment pulado: sem Parent ID.id ou Amount');
        results.skipped++;
        continue;
      }
      
      // Buscar deal
      const deal = dealMap.get(parentDealZohoId);
      console.log(`🔍 Deal matching: zoho_id="${parentDealZohoId}" -> deal="${deal?.id}"`);
      
      if (!deal) {
        results.errors.push(`Payment ${zohoPayment['Record Id']}: Deal não encontrado (${parentDealZohoId})`);
        results.skipped++;
        continue;
      }
      
      // Parse payment data
      const amount = parseAmount(zohoPayment['Amount'] || '0');
      const paymentDate = parseZohoDate(zohoPayment['Start/Payment Date']);
      const numberOfPayments = parseInt(zohoPayment['Number of Payments'] || '1');
      const paymentTotal = parseAmount(zohoPayment['Payment Total'] || zohoPayment['Amount'] || '0');
      
      // Skip se não tem valor
      if (amount === 0) {
        results.skipped++;
        continue;
      }
      
      // Criar objeto de payment info
      const paymentInfo = {
        zoho_payment_id: zohoPayment['Record Id'],
        amount,
        payment_date: paymentDate,
        payment_method: zohoPayment['Payment Method'] || null,
        payment_type: zohoPayment['Payment Type'] || null,
        number_of_payments: numberOfPayments,
        frequency: zohoPayment['Frequency'] || null,
        payment_total: paymentTotal,
        imported_at: new Date().toISOString(),
      };
      
      // Adicionar ao metadata.payments array do deal
      const metadata = deal.metadata || {};
      const payments = metadata.payments || [];
      
      // Verificar se payment já existe
      const existingIndex = payments.findIndex(
        (p: any) => p.zoho_payment_id === zohoPayment['Record Id']
      );
      
      if (existingIndex >= 0) {
        // UPDATE payment existente
        payments[existingIndex] = paymentInfo;
        results.updated++;
      } else {
        // CREATE novo payment
        payments.push(paymentInfo);
        results.created++;
      }
      
      metadata.payments = payments;
      
      // Atualizar deal com metadata atualizado
      const { error } = await supabase
        .from('deals')
        .update({ metadata })
        .eq('id', deal.id);
      
      if (error) {
        results.errors.push(`Erro ao atualizar deal com payment: ${error.message}`);
      } else {
        // Atualizar notes do deal com os payments
        await updateDealNotesWithPayments(deal.id);
      }
      
    } catch (err: any) {
      results.errors.push(`Erro ao processar payment ${zohoPayment['Record Id']}: ${err.message}`);
    }
  }

  console.log('✅ Importação de payments concluída:', results);
  return results;
}

// Função auxiliar para calcular próxima data de pagamento
function getNextPaymentDate(currentDate: string, frequency: string): string | null {
  const date = new Date(currentDate);
  
  switch (frequency?.toLowerCase()) {
    case 'weekly':
      date.setDate(date.getDate() + 7);
      break;
    case 'bi-weekly':
    case 'biweekly':
      date.setDate(date.getDate() + 14);
      break;
    case 'monthly':
      date.setMonth(date.getMonth() + 1);
      break;
    case 'quarterly':
      date.setMonth(date.getMonth() + 3);
      break;
    default: // One-time
      return null;
  }
  
  return date.toISOString().split('T')[0];
}

// Helper para atualizar notes do deal com payments
async function updateDealNotesWithPayments(dealId: string) {
  // Buscar deal com metadata
  const { data: deal } = await supabase
    .from('deals')
    .select('notes, metadata')
    .eq('id', dealId)
    .single();
  
  const metadata = deal?.metadata as any;
  if (!deal || !metadata?.payments) return;
  
  const payments = metadata.payments as any[];
  
  // Construir cronograma expandido de payments
  let paymentsSection = '---\n\n## 💰 Payment Schedule\n\n';
  
  let scheduleNumber = 1;
  let totalPayments = 0;
  
  payments.forEach((payment: any) => {
    const installments = payment.number_of_payments || 1;
    const amount = payment.amount || 0;
    const startDate = payment.payment_date;
    const method = payment.payment_method || 'Not specified';
    const type = payment.payment_type || 'Payment';
    const frequency = payment.frequency || 'One-time';
    
    // Gerar cada parcela individual
    let currentDate = startDate;
    for (let i = 0; i < installments; i++) {
      paymentsSection += `### ${scheduleNumber}. ${type}${installments > 1 ? ` (${i + 1}/${installments})` : ''}\n`;
      paymentsSection += `- **Amount:** $${amount.toLocaleString()}\n`;
      if (currentDate) paymentsSection += `- **Due Date:** ${currentDate}\n`;
      paymentsSection += `- **Method:** ${method}\n`;
      if (installments > 1) paymentsSection += `- **Series:** ${frequency}, ${i + 1} of ${installments}\n`;
      paymentsSection += '\n';
      
      totalPayments += amount;
      scheduleNumber++;
      
      // Calcular próxima data se não for a última parcela
      if (i < installments - 1 && currentDate) {
        currentDate = getNextPaymentDate(currentDate, frequency);
      }
    }
  });
  
  paymentsSection += `**Total Payments:** $${totalPayments.toLocaleString()}\n`;
  paymentsSection += `**Total Installments:** ${scheduleNumber - 1}\n`;
  
  // Manter notes existentes e adicionar/substituir payments
  let existingNotes = deal.notes || '';
  
  // Remover seções antigas de payments se existir
  existingNotes = existingNotes.replace(/---\n\n## 💰 Payment Schedule[\s\S]*?(?=\n\n##|$)/g, '');
  existingNotes = existingNotes.replace(/\n\n=== PAYMENT PLAN ===[\s\S]*?(?=\n\n===|$)/g, '');
  
  const updatedNotes = existingNotes + (existingNotes ? '\n\n' : '') + paymentsSection;
  
  // Atualizar deal
  await supabase
    .from('deals')
    .update({ notes: updatedNotes.trim() })
    .eq('id', dealId);
}

// ============================================
// GERAR/ATUALIZAR NOTAS PARA TODOS OS DEALS
// ============================================
export async function importNotes(_: any[]) {
  console.log('🔄 Analisando deals que precisam de atualização de notas com items...');
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    notesCreated: 0,
    errors: [] as string[],
  };

  // Buscar apenas deals que têm items
  const { data: dealsWithItems, error: itemsError } = await supabase
    .from('items')
    .select('deal_id')
    .not('deal_id', 'is', null);

  if (itemsError) {
    results.errors.push(`Erro ao buscar items: ${itemsError.message}`);
    return results;
  }

  const dealIdsWithItems = [...new Set(dealsWithItems?.map(i => i.deal_id) || [])];
  console.log(`📊 Encontrados ${dealIdsWithItems.length} deals com items`);

  if (dealIdsWithItems.length === 0) {
    console.log('⚠️ Nenhum deal com items encontrado');
    return results;
  }

  // Buscar deals que precisam de atualização (que têm items mas não têm a seção nas notas)
  const { data: deals, error } = await supabase
    .from('deals')
    .select('id, notes')
    .in('id', dealIdsWithItems);

  if (error) {
    results.errors.push(`Erro ao buscar deals: ${error.message}`);
    return results;
  }

  const dealsToUpdate = deals?.filter(d => {
    const notes: string = d.notes || '';
    return !notes.includes('## 📋 Services/Items');
  }) || [];

  console.log(`📝 ${dealsToUpdate.length} deals precisam ser atualizados`);

  let processed = 0;
  for (const deal of dealsToUpdate) {
    try {
      await updateDealNotesWithItems(deal.id);
      results.updated++;
      results.notesCreated++;
      processed++;
      
      // Log de progresso a cada 100 deals
      if (processed % 100 === 0) {
        console.log(`⏳ Progresso: ${processed}/${dealsToUpdate.length} deals processados`);
      }
    } catch (err: any) {
      results.errors.push(`Erro ao atualizar notas do deal ${deal.id}: ${err.message}`);
    }
  }

  // Deals que já tinham a seção
  results.skipped = dealIdsWithItems.length - dealsToUpdate.length;

  console.log('✅ Geração de notas concluída:', results);
  return results;
}

// ============================================
// IMPORTAÇÃO DE CONTACT NOTES
// ============================================
export async function importContactNotes(notes: ZohoNote[], onProgress?: (current: number, total: number) => void) {
  console.log(`🔄 Iniciando importação de ${notes.length} notas de contatos...`);
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [] as string[],
  };

  // Rastreamento detalhado de skip reasons
  const skipReasons = {
    noParentId: 0,
    contactNotFound: 0,
    emptyContent: 0,
    invalidData: 0,
  };

  // 1. Buscar contacts para matching (COM PAGINAÇÃO para garantir TODOS)
  console.log('📥 Carregando TODOS os contacts...');
  const contactMap = new Map<string, string>();
  
  let page = 0;
  const pageSize = 1000;
  let hasMore = true;
  
  while (hasMore) {
    const { data: contacts, error } = await supabase
      .from('contacts')
      .select('id, zoho_contact_id')
      .range(page * pageSize, (page + 1) * pageSize - 1);
    
    if (error) {
      console.error('❌ Erro ao carregar contacts:', error);
      break;
    }
    
    if (!contacts || contacts.length === 0) {
      hasMore = false;
      break;
    }
    
    contacts.forEach(c => {
      if (c.zoho_contact_id) contactMap.set(c.zoho_contact_id, c.id);
    });
    
    console.log(`📄 Página ${page + 1}: ${contacts.length} contacts carregados`);
    page++;
    hasMore = contacts.length === pageSize;
  }
  
  console.log(`👥 Total de contacts mapeados: ${contactMap.size}`);

  // 2. Buscar app_users para matching por nome (COM PAGINAÇÃO)
  console.log('📥 Carregando TODOS os usuários...');
  const userMapByZoho = new Map<string, string>();
  const userMapByName = new Map<string, string>();
  
  page = 0;
  hasMore = true;
  
  while (hasMore) {
    const { data: appUsers, error } = await supabase
      .from('app_users')
      .select('id, first_name, last_name, display_name, zoho_user_id')
      .range(page * pageSize, (page + 1) * pageSize - 1);
    
    if (error) {
      console.error('❌ Erro ao carregar users:', error);
      break;
    }
    
    if (!appUsers || appUsers.length === 0) {
      hasMore = false;
      break;
    }
    
    appUsers.forEach(u => {
      if (u.zoho_user_id) userMapByZoho.set(u.zoho_user_id, u.id);
      
      const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
      if (fullName) userMapByName.set(fullName, u.id);
      if (u.display_name) userMapByName.set(u.display_name, u.id);
    });
    
    console.log(`📄 Página ${page + 1}: ${appUsers.length} users carregados`);
    page++;
    hasMore = appUsers.length === pageSize;
  }
  
  console.log(`🧑‍💼 Total de users mapeados: ${userMapByZoho.size} por Zoho ID, ${userMapByName.size} por nome`);

  // 3. Processar notas e preparar lotes
  console.log('🔄 Processando notas...');
  const notesToInsert: any[] = [];
  let processedCount = 0;

  // Set para rastrear Parent IDs únicos
  const uniqueParentIds = new Set<string>();
  const parentIdsNotFound: string[] = [];

  for (const zohoNote of notes) {
    try {
      processedCount++;
      
      if (processedCount % 500 === 0) {
        console.log(`⏳ Processando ${processedCount}/${notes.length} notas (${Math.round(processedCount/notes.length*100)}%)`);
        console.log(`📊 Estatísticas parciais:`, {
          notesToInsert: notesToInsert.length,
          skipReasons: { ...skipReasons },
        });
        onProgress?.(processedCount, notes.length);
      }

      const zohoNoteId = zohoNote['Record Id'];
      const parentContactZohoId = zohoNote['Parent ID.id'];
      
      // Log detalhado para nota específica (Thiago)
      if (parentContactZohoId === 'zcrm_6338356000004896782') {
        console.log(`🔍 NOTA DO THIAGO ENCONTRADA:`, {
          recordId: zohoNoteId,
          parentId: parentContactZohoId,
          title: zohoNote['Note Title'],
          contentPreview: zohoNote['Note Content']?.substring(0, 100),
        });
      }
      
      if (!parentContactZohoId || parentContactZohoId.trim() === '') {
        skipReasons.noParentId++;
        results.skipped++;
        continue;
      }

      uniqueParentIds.add(parentContactZohoId);
      
      const contactId = contactMap.get(parentContactZohoId);
      if (!contactId) {
        skipReasons.contactNotFound++;
        results.skipped++;
        if (parentIdsNotFound.length < 100) {
          parentIdsNotFound.push(parentContactZohoId);
        }
        // Log detalhado para nota específica (Thiago)
        if (parentContactZohoId === 'zcrm_6338356000004896782') {
          console.log(`❌ CONTATO DO THIAGO NÃO ENCONTRADO!`, {
            parentId: parentContactZohoId,
            contactMapSize: contactMap.size,
            exists: contactMap.has(parentContactZohoId),
          });
        }
        continue;
      }
      
      const createdByZohoId = zohoNote['Created By.id'];
      const createdByName = zohoNote['Created By'];
      const createdByUserId = userMapByZoho.get(createdByZohoId) || userMapByName.get(createdByName) || null;
      
      const modifiedByZohoId = zohoNote['Modified By.id'];
      const modifiedByName = zohoNote['Modified By'];
      const modifiedByUserId = userMapByZoho.get(modifiedByZohoId) || userMapByName.get(modifiedByName) || null;
      
      const cleanContent = stripHtmlTags(zohoNote['Note Content']);
      
      if (!cleanContent || cleanContent.trim() === '') {
        skipReasons.emptyContent++;
        results.skipped++;
        continue;
      }
      
      // Log detalhado para nota específica (Thiago)
      if (parentContactZohoId === 'zcrm_6338356000004896782') {
        console.log(`✅ NOTA DO THIAGO SERÁ INSERIDA:`, {
          contactId,
          contentLength: cleanContent.length,
          title: zohoNote['Note Title'],
        });
      }
      
      notesToInsert.push({
        contact_id: contactId,
        title: zohoNote['Note Title'] || null,
        content: cleanContent,
        created_at: parseZohoDate(zohoNote['Created Time']) || new Date().toISOString(),
        created_by: createdByUserId,
        modified_at: parseZohoDate(zohoNote['Modified Time']) || null,
        modified_by: modifiedByUserId,
        zoho_note_id: zohoNoteId,
        zoho_parent_id: parentContactZohoId,
      });
      
    } catch (err: any) {
      skipReasons.invalidData++;
      results.errors.push(`Erro ao processar nota ${zohoNote['Record Id']}: ${err.message}`);
    }
  }

  // Log de estatísticas detalhadas
  console.log(`📊 ESTATÍSTICAS DE PROCESSAMENTO:`, {
    totalLinhas: notes.length,
    processadas: processedCount,
    notasParaInserir: notesToInsert.length,
    uniqueParentIds: uniqueParentIds.size,
    skipReasons: { ...skipReasons },
    parentIdsNotFoundSample: parentIdsNotFound.slice(0, 10),
  });

  // 4. Inserir em lotes de 100
  console.log(`💾 Inserindo ${notesToInsert.length} notas em lotes de 100...`);
  const BATCH_SIZE = 100;
  let insertedCount = 0;

  for (let i = 0; i < notesToInsert.length; i += BATCH_SIZE) {
    const batch = notesToInsert.slice(i, i + BATCH_SIZE);
    
    try {
      const { data, error } = await supabase
        .from('notes')
        .upsert(batch, {
          onConflict: 'zoho_note_id',
          ignoreDuplicates: false
        })
        .select('id');
      
      if (error) {
        console.error(`❌ Erro no batch ${i}-${i+batch.length}:`, error);
        results.errors.push(`Batch ${i}-${i+batch.length}: ${error.message}`);
      } else {
        insertedCount += batch.length;
        results.created += batch.length;
        console.log(`✅ Batch ${Math.floor(i/BATCH_SIZE) + 1}/${Math.ceil(notesToInsert.length/BATCH_SIZE)}: ${batch.length} notas`);
        onProgress?.(processedCount + insertedCount, notes.length);
      }
    } catch (err: any) {
      console.error(`❌ Exceção no batch ${i}:`, err);
      results.errors.push(`Batch ${i}: ${err.message}`);
    }
  }

  // Adicionar estatísticas detalhadas ao resultado
  const finalResults = {
    ...results,
    skipReasons,
    statistics: {
      totalLinhas: notes.length,
      processadas: processedCount,
      notasParaInserir: notesToInsert.length,
      uniqueParentIds: uniqueParentIds.size,
      parentIdsNotFoundSample: parentIdsNotFound.slice(0, 10),
    }
  };

  console.log('✅ Importação de notas de contatos concluída:', finalResults);
  return finalResults;
}

// ============================================
// IMPORTAÇÃO DE DEAL NOTES
// ============================================
export async function importDealNotes(notes: ZohoNote[], onProgress?: (current: number, total: number) => void) {
  console.log(`🔄 Iniciando importação de ${notes.length} notas de deals...`);
  
  const results = {
    created: 0,
    updated: 0,
    skipped: 0,
    errors: [] as string[],
  };

  // Helper para normalizar IDs do Zoho
  const normalizeZohoId = (id: string): string => {
    if (!id) return '';
    return id.replace(/^(zcrm_|crm_)/i, '').trim();
  };

  // 1. Buscar todos os deals para mapping (zoho_deal_id -> id) COM PAGINAÇÃO
  console.log('📥 Carregando deals com paginação...');
  const dealMap = new Map<string, string>();
  const dealMapNormalized = new Map<string, string>();
  
  const PAGE_SIZE = 1000;
  let page = 0;
  let hasMore = true;
  let totalDeals = 0;
  
  while (hasMore) {
    const { data: dealsPage, error: dealsError } = await supabase
      .from('deals')
      .select('id, zoho_deal_id')
      .not('zoho_deal_id', 'is', null)
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    
    if (dealsError) {
      console.error('❌ Erro ao buscar deals:', dealsError);
      throw dealsError;
    }
    
    const pageSize = dealsPage?.length || 0;
    console.log(`📄 Página ${page + 1}: ${pageSize} deals carregados`);
    
    dealsPage?.forEach(deal => {
      if (deal.zoho_deal_id) {
        dealMap.set(deal.zoho_deal_id, deal.id);
        // Também adiciona versão normalizada
        const normalized = normalizeZohoId(deal.zoho_deal_id);
        if (normalized) {
          dealMapNormalized.set(normalized, deal.id);
        }
      }
    });
    
    totalDeals += pageSize;
    hasMore = pageSize === PAGE_SIZE;
    page++;
  }
  
  console.log(`💼 Total de deals mapeados: ${dealMap.size} (${page} páginas)`);
  
  // 🔍 DEBUG: Deal específico do Thiago
  const DEBUG_DEAL_ID = 'zcrm_6338356000014569382';
  console.log(`🔍 DEBUG: Procurando deal ${DEBUG_DEAL_ID} no mapa...`);
  console.log(`✅ Deal encontrado no mapa: ${dealMap.has(DEBUG_DEAL_ID)}`);
  if (dealMap.has(DEBUG_DEAL_ID)) {
    console.log(`📌 UUID do deal: ${dealMap.get(DEBUG_DEAL_ID)}`);
  } else {
    console.log(`❌ Deal ${DEBUG_DEAL_ID} NÃO está no mapa!`);
    console.log(`🔎 Primeiros 10 Zoho Deal IDs no mapa:`, Array.from(dealMap.keys()).slice(0, 10));
  }

  // 2. Buscar app_users para matching COM PAGINAÇÃO
  console.log('📥 Carregando usuários com paginação...');
  const userMapByZoho = new Map<string, string>();
  const userMapByName = new Map<string, string>();
  
  page = 0;
  hasMore = true;
  let totalUsers = 0;
  
  while (hasMore) {
    const { data: usersPage, error: usersError } = await supabase
      .from('app_users')
      .select('id, first_name, last_name, display_name, zoho_user_id')
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    
    if (usersError) {
      console.error('❌ Erro ao buscar usuários:', usersError);
      throw usersError;
    }
    
    const pageSize = usersPage?.length || 0;
    console.log(`📄 Página ${page + 1}: ${pageSize} usuários carregados`);
    
    usersPage?.forEach(u => {
      if (u.zoho_user_id) userMapByZoho.set(u.zoho_user_id, u.id);
      
      const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
      if (fullName) userMapByName.set(fullName, u.id);
      if (u.display_name) userMapByName.set(u.display_name, u.id);
    });
    
    totalUsers += pageSize;
    hasMore = pageSize === PAGE_SIZE;
    page++;
  }
  
  console.log(`🧑‍💼 Total de usuários mapeados: ${userMapByZoho.size} por Zoho ID, ${userMapByName.size} por nome (${page} páginas)`);


  // 3. Processar notas e preparar lotes
  console.log('🔄 Processando notas...');
  const notesToInsert: any[] = [];
  let processedCount = 0;
  const parentIdsNotFound: string[] = [];
  let debugDealNotesCount = 0;

  for (const zohoNote of notes) {
    try {
      processedCount++;
      
      if (processedCount % 100 === 0) {
        console.log(`⏳ Processando ${processedCount}/${notes.length} notas (${Math.round(processedCount/notes.length*100)}%)`);
        onProgress?.(processedCount, notes.length);
      }

      const zohoNoteId = zohoNote['Record Id'];
      const parentDealZohoId = zohoNote['Parent ID.id'];
      
      if (!parentDealZohoId) {
        results.skipped++;
        continue;
      }
      
      // 🔍 DEBUG: Tracking do deal específico
      if (parentDealZohoId === DEBUG_DEAL_ID) {
        debugDealNotesCount++;
        console.log(`🔍 DEBUG: Nota ${debugDealNotesCount} para deal ${DEBUG_DEAL_ID}`, {
          zohoNoteId,
          parentDealZohoId,
          noteTitle: zohoNote['Note Title'],
          contentLength: (zohoNote['Note Content'] || '').length,
        });
      }
      
      // Matching resiliente: tenta direto primeiro, depois normalizado
      let dealId = dealMap.get(parentDealZohoId);
      
      if (!dealId) {
        // Tenta matching normalizado
        const normalized = normalizeZohoId(parentDealZohoId);
        dealId = dealMapNormalized.get(normalized);
        
        if (dealId && parentDealZohoId === DEBUG_DEAL_ID) {
          console.log(`✅ DEBUG: Deal ${DEBUG_DEAL_ID} encontrado via normalização!`);
        }
      }
      
      if (!dealId) {
        results.skipped++;
        
        // 🔍 DEBUG: Log de Parent IDs não encontrados
        if (parentIdsNotFound.length < 20) {
          parentIdsNotFound.push(parentDealZohoId);
        }
        
        if (parentDealZohoId === DEBUG_DEAL_ID) {
          console.log(`❌ DEBUG: Deal ${DEBUG_DEAL_ID} NÃO encontrado no mapa! Nota será pulada.`);
          console.log(`🔍 DEBUG: Formato do Parent ID.id no CSV: "${parentDealZohoId}"`);
          console.log(`🔍 DEBUG: Normalized ID: "${normalizeZohoId(parentDealZohoId)}"`);
          console.log(`❌ Nenhum match encontrado mesmo com normalização`);
        }
        
        continue;
      }
      
      const createdByZohoId = zohoNote['Created By.id'];
      const createdByName = zohoNote['Created By'];
      const createdByUserId = userMapByZoho.get(createdByZohoId) || userMapByName.get(createdByName) || null;
      
      const modifiedByZohoId = zohoNote['Modified By.id'];
      const modifiedByName = zohoNote['Modified By'];
      const modifiedByUserId = userMapByZoho.get(modifiedByZohoId) || userMapByName.get(modifiedByName) || null;
      
      const cleanContent = stripHtmlTags(zohoNote['Note Content']);
      
      if (!cleanContent || cleanContent.trim() === '') {
        results.skipped++;
        if (parentDealZohoId === DEBUG_DEAL_ID) {
          console.log(`⚠️ DEBUG: Nota do deal ${DEBUG_DEAL_ID} pulada por conteúdo vazio`);
        }
        continue;
      }
      
      // 🔍 DEBUG: Nota será inserida
      if (parentDealZohoId === DEBUG_DEAL_ID) {
        console.log(`✅ DEBUG: Nota do deal ${DEBUG_DEAL_ID} SERÁ INSERIDA`, {
          dealId,
          contentLength: cleanContent.length,
          title: zohoNote['Note Title'],
        });
      }
      
      notesToInsert.push({
        deal_id: dealId,
        title: zohoNote['Note Title'] || null,
        content: cleanContent,
        created_at: parseZohoDate(zohoNote['Created Time']) || new Date().toISOString(),
        created_by: createdByUserId,
        modified_at: parseZohoDate(zohoNote['Modified Time']) || null,
        modified_by: modifiedByUserId,
        zoho_note_id: zohoNoteId,
        zoho_parent_id: parentDealZohoId,
      });
      
    } catch (err: any) {
      results.errors.push(`Erro ao processar nota ${zohoNote['Record Id']}: ${err.message}`);
    }
  }

  // 📊 Log de estatísticas de debug
  console.log(`📊 ESTATÍSTICAS DE PROCESSAMENTO:`, {
    totalLinhas: notes.length,
    notasParaInserir: notesToInsert.length,
    notasPuladas: results.skipped,
    debugDealNotesCount,
    parentIdsNotFoundSample: parentIdsNotFound.slice(0, 10),
  });

  // 4. Inserir em lotes de 100
  console.log(`💾 Inserindo ${notesToInsert.length} notas em lotes de 100...`);
  const BATCH_SIZE = 100;
  let insertedCount = 0;

  for (let i = 0; i < notesToInsert.length; i += BATCH_SIZE) {
    const batch = notesToInsert.slice(i, i + BATCH_SIZE);
    
    try {
      const { data, error } = await supabase
        .from('notes')
        .upsert(batch, {
          onConflict: 'zoho_note_id',
          ignoreDuplicates: false
        })
        .select('id');
      
      if (error) {
        console.error(`❌ Erro no batch ${i}-${i+batch.length}:`, error);
        results.errors.push(`Batch ${i}-${i+batch.length}: ${error.message}`);
      } else {
        insertedCount += batch.length;
        results.created += batch.length;
        console.log(`✅ Batch ${Math.floor(i/BATCH_SIZE) + 1}/${Math.ceil(notesToInsert.length/BATCH_SIZE)}: ${batch.length} notas`);
        onProgress?.(processedCount + insertedCount, notes.length);
      }
    } catch (err: any) {
      console.error(`❌ Exceção no batch ${i}:`, err);
      results.errors.push(`Batch ${i}: ${err.message}`);
    }
  }

  console.log('✅ Importação de notas de deals concluída:', results);
  return results;
}
